import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useEffect, useId, useState } from "react";
import { Link, type LinkProps } from "react-router-dom";

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
}) {
  return (
    <button
      type="button"
      className={`ui-button ui-button--${variant} ui-button--${size} ${className}`}
      {...props}
    />
  );
}

export function AppLink({ className = "", ...props }: LinkProps) {
  return <Link className={`ui-link ${className}`} {...props} />;
}

function Field({
  label,
  hint,
  error,
  inputId,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  inputId: string;
  children: ReactNode;
}) {
  return (
    <label className="ui-field" htmlFor={inputId}>
      <span className="ui-field__label">{label}</span>
      {children}
      {error ? (
        <span className="ui-field__error">{error}</span>
      ) : hint ? (
        <span className="ui-field__hint">{hint}</span>
      ) : null}
    </label>
  );
}

export function Input({
  label,
  hint,
  error,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <Field label={label} hint={hint} error={error} inputId={inputId}>
      <input id={inputId} className="ui-control" aria-invalid={Boolean(error)} {...props} />
    </Field>
  );
}

export function Select({
  label,
  hint,
  error,
  id,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: string;
  error?: string;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <Field label={label} hint={hint} error={error} inputId={inputId}>
      <select id={inputId} className="ui-control" aria-invalid={Boolean(error)} {...props}>
        {children}
      </select>
    </Field>
  );
}

export function Textarea({
  label,
  hint,
  error,
  id,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: string;
  error?: string;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <Field label={label} hint={hint} error={error} inputId={inputId}>
      <textarea
        id={inputId}
        className="ui-control ui-textarea"
        aria-invalid={Boolean(error)}
        {...props}
      />
    </Field>
  );
}

export function Dialog({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="ui-layer" role="presentation">
      <section className="ui-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Đóng">
            Đóng
          </Button>
        </header>
        {children}
      </section>
    </div>
  );
}

export function Drawer({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="ui-layer" role="presentation">
      <aside className="ui-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </header>
        {children}
      </aside>
    </div>
  );
}

export function Table({
  headers,
  rows,
  caption,
}: {
  headers: string[];
  rows: ReactNode[][];
  caption: string;
}) {
  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        <caption>{caption}</caption>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="ui-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "success" | "warning" | "danger" | "info";
  children: ReactNode;
}) {
  return <span className={`ui-badge ui-badge--${tone}`}>{children}</span>;
}

export function Toast({
  title,
  message,
  tone = "success",
  onClose,
}: {
  title: string;
  message: string;
  tone?: "success" | "danger" | "info";
  onClose: () => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(onClose, 5000);
    return () => window.clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`ui-toast ui-toast--${tone}`} role="status">
      <div>
        <strong>{title}</strong>
        <p>{message}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onClose}>Đóng</Button>
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="ui-skeleton" role="status" aria-label="Đang tải">
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <section className="ui-state">
      <span className="ui-state__icon" aria-hidden="true">—</span>
      <h2>{title}</h2>
      <p>{description}</p>
      {action ? <Button onClick={action.onClick}>{action.label}</Button> : null}
    </section>
  );
}

export function ErrorState({
  title,
  description,
  requestId,
  action,
}: {
  title: string;
  description: string;
  requestId?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <section className="ui-state ui-state--error">
      <span className="ui-state__icon" aria-hidden="true">!</span>
      <h2>{title}</h2>
      <p>{description}</p>
      {requestId ? <code>Mã yêu cầu: {requestId}</code> : null}
      {action ? <Button onClick={action.onClick}>{action.label}</Button> : null}
    </section>
  );
}

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  return (
    <nav className="ui-pagination" aria-label="Phân trang">
      <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Trước
      </Button>
      <span>Trang {page}/{totalPages}</span>
      <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        Sau
      </Button>
    </nav>
  );
}

export function ComponentDemoState() {
  const [dialog, setDialog] = useState(false);
  const [toast, setToast] = useState(false);
  const [tab, setTab] = useState("overview");
  const [page, setPage] = useState(1);

  return (
    <div className="component-demo">
      <div className="button-row">
        <Button onClick={() => setDialog(true)}>Mở dialog</Button>
        <Button variant="secondary" onClick={() => setToast(true)}>Hiện toast</Button>
        <Button variant="danger">Hành động nguy hiểm</Button>
      </div>
      <div className="form-grid">
        <Input label="Tên hiển thị" placeholder="Nhập tên" hint="Tối đa 80 ký tự" />
        <Select label="Loại tài khoản" defaultValue="member">
          <option value="member">Thành viên</option>
          <option value="breeder">Kennel / Breeder</option>
        </Select>
        <Textarea label="Ghi chú" placeholder="Nhập nội dung" />
      </div>
      <Tabs
        tabs={[
          { id: "overview", label: "Tổng quan" },
          { id: "status", label: "Trạng thái" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Pagination page={page} totalPages={4} onChange={setPage} />
      <Dialog open={dialog} title="Dialog nền tảng" onClose={() => setDialog(false)}>
        <p>Dialog dùng component chuẩn, không dùng native overlay phát sinh.</p>
      </Dialog>
      {toast ? (
        <Toast
          title="Đã lưu"
          message="Trạng thái success đã được kiểm tra."
          onClose={() => setToast(false)}
        />
      ) : null}
    </div>
  );
}
